import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowRight, Eye, Award, Zap } from 'lucide-react';
import { serviceImages } from '../mock';
import { Link } from 'react-router-dom';

const Showcase = () => {
  const showcaseItems = [
    {
      id: 1,
      title: "Ingénierie Radio",
      description: "Conception et optimisation des réseaux mobiles de nouvelle génération",
      image: serviceImages.radioEngineering,
      category: "Télécoms",
      icon: Zap,
      stats: "13+ projets QoS"
    },
    {
      id: 2,
      title: "Audit QoS Avancé",
      description: "Analyse approfondie et benchmarking des performances réseau",
      image: serviceImages.auditQos,
      category: "Audit",
      icon: Eye,
      stats: "255+ localités auditées"
    },
    {
      id: 3,
      title: "Data Center Moderne",
      description: "Infrastructure haute disponibilité et virtualisation avancée",
      image: serviceImages.dataCenter,
      category: "Infrastructure",
      icon: Award,
      stats: "6+ clients satisfaits"
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Télécoms': return 'bg-blue-100 text-blue-800';
      case 'Audit': return 'bg-orange-100 text-orange-800';
      case 'Infrastructure': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-slate-100 text-slate-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Award className="w-4 h-4 mr-2" />
            Nos Réalisations
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            Excellence Technique en Action
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez nos projets d'envergure et notre expertise technique 
            à travers des réalisations concrètes et mesurables.
          </p>
        </div>

        {/* Showcase Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {showcaseItems.map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.id} className="group hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-200">
                {/* Image with overlay */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                  
                  {/* Overlay Content */}
                  <div className="absolute top-4 left-4">
                    <Badge className={getCategoryColor(item.category)}>
                      {item.category}
                    </Badge>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium">{item.stats}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {item.description}
                  </p>
                  
                  <Button 
                    variant="ghost" 
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 p-0 group"
                    asChild
                  >
                    <Link to="/projects">
                      Voir le projet
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
              Votre Projet, Notre Expertise
            </h3>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Chaque projet est unique. Découvrez comment notre équipe d'experts 
              peut transformer vos défis techniques en solutions innovantes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-medium"
              >
                <Link to="/contact">
                  Démarrer un Projet
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline" 
                size="lg"
                className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-4 font-medium"
              >
                <Link to="/projects">
                  Voir tous nos Projets
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Showcase;